/** A `fetch_url` step (design §6.7): the request is read by the SERVER from the
    saved model — a named integration connection, or a full URL on an
    allow-listed host — never a URL the browser hands over. The URL TESTER runs
    the request through the same server operation, so the SSRF guard, the
    timeout and the JSONPath mapping are the real ones. */

import { useEffect, useState } from 'react'
import { FlaskConical, Loader2 } from 'lucide-react'
import { parseApiError } from '@twasta/page-runtime/txn'
import { testWizardFetch } from '@/api/wizards'
import { listHttpConnectionNames } from '@/api/httpConnections'
import type { WizardFetchResponse } from '@twasta/page-runtime/wizard/contract'
import ExprInput from './ExprInput'
import { KeyValueEditor, LineFormPicker, RouteStringPicker, TargetPicker } from './pickers'
import { formByNo, lineFormFor, useWizardDesign } from './context'
import type { WizardFetchRequest, WizardStep } from './types'
import {
  Badge, Field, Grid2, NumberInput, SelectInput, Sub, TextInput, hintStyle, monoAreaStyle,
  parseJsonOrNull, smallBtn,
} from './ui'

function UrlTester({ step }: { step: WizardStep }) {
  const ctx = useWizardDesign()
  const [busy, setBusy] = useState(false)
  const [res, setRes] = useState<WizardFetchResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [frameText, setFrameText] = useState('{\n  "header": {},\n  "line": {}\n}')
  const frame = parseJsonOrNull(frameText)
  const run = async () => {
    setBusy(true)
    setError(null)
    setRes(null)
    try {
      setRes(await testWizardFetch(ctx.project, ctx.model, step.id,
        frame && typeof frame === 'object' ? frame as never : undefined))
    } catch (err) {
      setError(parseApiError(err).message || 'The request failed')
    } finally {
      setBusy(false)
    }
  }
  return (
    <div>
      <Field label="Sample values the request's templates read (JSON: header / line / var / input)">
        <textarea value={frameText} onChange={(e) => setFrameText(e.target.value)} spellCheck={false}
          style={{ ...monoAreaStyle, minHeight: 70 }} />
        {frame === null && <div style={{ ...hintStyle, color: 'var(--color-error, #d33)' }}>Not valid JSON.</div>}
      </Field>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <button type="button" style={smallBtn} onClick={run} disabled={busy || frame === null}>
          {busy ? <Loader2 size={12} style={{ animation: 'spin 1s linear infinite' }} /> : <FlaskConical size={12} />}
          Test request
        </button>
        {res && (res.ok ? <Badge tone="ok">HTTP {res.status ?? 'ok'}</Badge> : <Badge tone="error">{res.error || `HTTP ${res.status ?? '?'}`}</Badge>)}
      </div>
      {error && <div style={{ ...hintStyle, color: 'var(--color-error, #d33)' }}>{error}</div>}
      {res && (
        <pre style={{ ...monoAreaStyle, minHeight: 0, maxHeight: 220, overflow: 'auto', marginTop: 6, whiteSpace: 'pre-wrap' }}>
          {JSON.stringify({ sets: res.sets, lines: res.lines }, null, 2)}
        </pre>
      )}
      <div style={hintStyle}>The test reads the model as it is on screen (saved or not).</div>
    </div>
  )
}

/** The application's named HTTP connections (Advanced → HTTP connections)
 *  as a picker; free text when none are configured or they cannot be read. */
function ConnectionPicker({ project, value, onChange }: {
  project: string
  value: string | undefined
  onChange: (v: string) => void
}) {
  const [names, setNames] = useState<string[] | null>(null)
  useEffect(() => {
    let live = true
    void listHttpConnectionNames(project).then((n) => { if (live) setNames(n) })
    return () => { live = false }
  }, [project])
  return (
    <>
      {names && names.length > 0
        ? <SelectInput value={value} options={names} onChange={onChange} empty="— pick a connection —" />
        : <TextInput value={value} onChange={onChange} placeholder="scale_gateway" />}
      <div style={hintStyle}>
        {names && names.length === 0
          ? 'No connections yet — add one under Advanced → HTTP connections.'
          : 'Base URL and keys are set under Advanced → HTTP connections.'}
      </div>
    </>
  )
}

/** Where an answer goes — a `fetch_url` response, a `function` step's result,
 *  a `redirect` step's reply: targets set from JSONPaths, and optionally an
 *  array made into lines. One editor, because the server maps all three the
 *  same way (wizard/fetch.py `map_answer`). */
export function AnswerMapping({ step, onChange, title = 'Set from the response (target → JSONPath)' }: {
  step: WizardStep
  onChange: (p: Partial<WizardStep>) => void
  title?: string
}) {
  const ctx = useWizardDesign()
  const lines = step.lines
  const linesForm = formByNo(ctx.base, lines?.form_no) || lineFormFor(ctx.base, ctx.model, step.form_no)
  return (
    <>
      <Sub title={title}>
        <KeyValueEditor value={step.map} onChange={(map) => onChange({ map })} valueMode="text" valuePlaceholder="$.weight.gross"
          keyRender={(k, setK) => <TargetPicker value={k} onChange={setK} formNo={step.form_no} />} />
      </Sub>
      <Sub title="Lines from a response array (optional)">
        {!lines ? (
          <button type="button" style={smallBtn} onClick={() => onChange({ lines: { path: '$.items[*]', map: {} } })}>
            Make a response array into lines
          </button>
        ) : (
          <>
            <Grid2>
              <Field label="Array (JSONPath)">
                <TextInput mono value={lines.path} onChange={(path) => onChange({ lines: { ...lines, path } })} />
              </Field>
              <Field label="Line form">
                <LineFormPicker value={lines.form_no} onChange={(form_no) => onChange({ lines: { ...lines, form_no: form_no || undefined } })} />
              </Field>
            </Grid2>
            <Field label="Columns (line column → JSONPath within an item)">
              <KeyValueEditor value={lines.map} onChange={(map) => onChange({ lines: { ...lines, map: map as Record<string, string> } })}
                keyOptions={(linesForm?.columns || []).map((c) => c.name)} valueMode="text" valuePlaceholder="$.sku" />
            </Field>
            <button type="button" style={smallBtn} onClick={() => onChange({ lines: undefined })}>No lines</button>
          </>
        )}
      </Sub>
    </>
  )
}

export default function FetchEditor({ step, onChange }: {
  step: WizardStep
  onChange: (p: Partial<WizardStep>) => void
}) {
  const ctx = useWizardDesign()
  const req: WizardFetchRequest = step.request || { method: 'GET' }
  const patchReq = (p: Partial<WizardFetchRequest>) => onChange({ request: { ...req, ...p } })
  const via = req.connection !== undefined || !req.url ? 'connection' : 'url'
  const [bodyText, setBodyText] = useState(() => (req.body === undefined ? '' : JSON.stringify(req.body, null, 2)))
  return (
    <div>
      <Sub title="Request">
        <Grid2>
          <Field label="Where">
            <SelectInput value={via}
              onChange={(v) => onChange({ request: v === 'connection'
                ? { method: req.method, connection: req.connection || '', path: req.path || '' }
                : { method: req.method, url: req.url || 'https://' } })}
              options={[{ value: 'connection', label: 'A named HTTP connection' }, { value: 'url', label: 'A full URL (allow-listed host)' }]} />
          </Field>
          <Field label="Method">
            <SelectInput value={req.method || 'GET'} options={['GET', 'POST']}
              onChange={(method) => patchReq({ method: method as 'GET' | 'POST' })} />
          </Field>
          {via === 'connection' ? (
            <>
              <Field label="Connection">
                <ConnectionPicker project={ctx.project} value={req.connection}
                  onChange={(connection) => patchReq({ connection })} />
              </Field>
              <Field label="Path (templates allowed)">
                <ExprInput mode="template" value={req.path} onChange={(path) => patchReq({ path })} formNo={step.form_no}
                  placeholder="/scales/{header.scale_id}/reading" />
              </Field>
            </>
          ) : (
            <Field label="URL (templates allowed)">
              <ExprInput mode="template" value={req.url} onChange={(url) => patchReq({ url })} formNo={step.form_no} />
            </Field>
          )}
          <Field label="Timeout (ms)">
            <NumberInput value={step.timeout_ms} min={100} onChange={(timeout_ms) => onChange({ timeout_ms })} />
          </Field>
          <Field label="Retry button label">
            <TextInput value={step.retry?.button} onChange={(button) => onChange({ retry: button ? { button } : undefined })}
              placeholder="Read again" />
          </Field>
          <Field label="On error">
            <RouteStringPicker value={step.on_error} onChange={(on_error) => onChange({ on_error: on_error || undefined })} />
          </Field>
        </Grid2>
        <Field label="Headers (name → template)">
          <KeyValueEditor value={req.headers} onChange={(headers) => patchReq({ headers: headers as Record<string, string> })}
            formNo={step.form_no} keyPlaceholder="Header" />
        </Field>
        {req.method === 'POST' && (
          <Field label="Body (JSON with templates in its strings)">
            <textarea value={bodyText} spellCheck={false} style={{ ...monoAreaStyle, minHeight: 80 }}
              onChange={(e) => {
                setBodyText(e.target.value)
                const parsed = parseJsonOrNull(e.target.value)
                if (parsed !== null) patchReq({ body: parsed })
              }} />
            {parseJsonOrNull(bodyText) === null && <div style={{ ...hintStyle, color: 'var(--color-error, #d33)' }}>Not valid JSON — not saved.</div>}
          </Field>
        )}
      </Sub>
      <AnswerMapping step={step} onChange={onChange} />
      <Sub title="URL tester">
        <UrlTester step={step} />
      </Sub>
    </div>
  )
}
